import React, { useState } from 'react';
import { X, User, Calendar, Download, Loader2 } from 'lucide-react';
import CombinedTaskViewer from './CombinedTaskViewer';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const CombinedTaskReviewModal = ({ isOpen, onClose, task, assignment }) => {
    const [isExporting, setIsExporting] = useState(false);

    if (!isOpen || !task || !assignment) return null;

    const handleExportPDF = async () => {
        setIsExporting(true);
        try {
            const token = localStorage.getItem('token');
            const assignmentId = assignment.id || assignment.assignment_id;
            
            const response = await api.get(`/tasks/assignment/${assignmentId}/export-pdf`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Extract filename from Content-Disposition header or generate one
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'task-document.pdf';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            } else {
                const taskTitle = (task.title || 'Task').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
                const employeeName = (assignment.employee_name || 'Employee').replace(/[^a-zA-Z0-9]/g, '_');
                const date = new Date().toISOString().split('T')[0];
                filename = `${taskTitle}_${employeeName}_${date}.pdf`;
            }
            
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success(`Downloaded: ${filename}`);
        } catch (error) {
            console.error('PDF export error:', error);
            toast.error('Failed to export PDF');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">
                <div className="p-5 border-b flex justify-between items-center bg-gray-50/50 rounded-t-xl">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            Review: {task.title}
                        </h2>
                        <div className="flex items-center gap-4 mt-1.5 text-sm text-gray-500">
                            <span className="flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5" />
                                {assignment.employee_name || 'Employee'}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(assignment.submitted_at || assignment.updated_at).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-0 bg-gray-50/30">
                    <div className="max-w-3xl mx-auto py-8 px-4">
                        <CombinedTaskViewer
                            task={task}
                            assignment={assignment}
                            isAdmin={true} // Triggers read-only mode
                        />
                    </div>
                </div>

                <div className="p-4 border-t bg-gray-50/50 rounded-b-xl flex justify-between items-center">
                    <button
                        onClick={handleExportPDF}
                        disabled={isExporting}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm flex items-center gap-2"
                    >
                        {isExporting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Exporting...</span>
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4" />
                                <span>Export Document</span>
                            </>
                        )}
                    </button>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors shadow-sm"
                    >
                        Close Preview
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CombinedTaskReviewModal;
